import type { ReactNode } from "react";
import React from "react";
import { styled } from "@linaria/react";
import { color, easingSoftMs, zIndex } from "..";
import {
  BANNER_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
  SECONDARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "../Header/useHeaderHeight";
import { colors } from "../Theme";
import { css } from "@linaria/core";
import cx from "classnames";
import ButtonIconV2 from "../ButtonIconV2";
import { H4 } from "../Text";
import { CloseLine } from "../IconV2/react";

const DRAWER_PANEL_WIDTH = 400;
const OPENED_CN = "opened";
const SECONDARY_HEADER_PRESENT = "secondary-header";

interface P {
  title: React.ReactNode;
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  onRequestClose: () => void;
}

function Drawer({ title, isOpen, onRequestClose, children, className, header }: P) {
  const drawerRef = React.useRef<HTMLDivElement>(null);

  return (
    <div ref={drawerRef} tabIndex={-1} className={cx(drawer, className)}>
      <Wrapper
        className={cx("drawer-wrapper", {
          [OPENED_CN]: isOpen,
        })}
      >
        {header ? (
          header
        ) : (
          <header className={drawerHeader}>
            {title !== undefined && <H4 className="title">{title}</H4>}
            <ButtonIconV2 onClick={onRequestClose} tabIndex={-1} className="close-button">
              <CloseLine size="l" color={colors.titanium800} />
            </ButtonIconV2>
          </header>
        )}
        {children}
      </Wrapper>
    </div>
  );
}

export default React.memo(Drawer);

const drawer = css`
  position: fixed;
  z-index: ${zIndex.sidePanel};
  right: 0;
  top: calc(var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px));
  bottom: 0;

  &.${SECONDARY_HEADER_PRESENT} {
    top: calc(
      var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) +
        var(${SECONDARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px)
    );
  }
`;

const Wrapper = styled.div`
  position: absolute;
  padding: 24px 16px 16px;
  background-color: ${color.hydrogen};
  border-left: 1px solid ${color.titanium200};

  display: flex;
  flex-direction: column;

  top: 0;
  bottom: 0;
  width: ${DRAWER_PANEL_WIDTH}px;
  right: -${DRAWER_PANEL_WIDTH}px;

  transition: right ${easingSoftMs}ms;

  &.${OPENED_CN} {
    right: 0px;
  }

  @media print {
    display: none;
    &.open {
      display: flex;
    }
  }
`;

const drawerHeader = css`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px;
  align-items: center;

  & > .title {
    color: ${colors.carbon900};
    overflow-wrap: break-word;
    hyphens: auto;
    width: 100%;
  }

  & > .close-button {
    align-self: start;
    height: 20px;
    width: 20px;
    grid-row: 1 / span 2;
    grid-column: 2 / -1;
  }
`;
